import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useBalancedEquationsStore } from '../store/useBalancedEquationsStore';
import LabRoom from '../../lab/shared/LabRoom';
import { BoilingTube } from '../../lab/shared/BoilingTube';
import { BunsenBurner } from '../../lab/shared/BunsenBurner';
import ClampStand from '../../lab/shared/ClampStand';
import RubberStopper from '../../lab/shared/RubberStopper';
import { RubberTube } from '../../lab/shared/RubberTube';
import { TestTubeHolder } from '../../lab/shared/TestTubeHolder';
import { CuCO3Powder } from '../../lab/shared/CuCO3Powder';
import { MilkyLiquid } from '../../lab/shared/MilkyLiquid';
import { WhiteSmokeCloud } from '../../lab/shared/WhiteSmokeCloud';
import { YellowPrecipitate } from '../../lab/shared/YellowPrecipitate';
import { HydrogenBubbles } from '../../lab/shared/HydrogenBubbles';
import {
  CuCO3PourAnimation,
  PartABuildAnimation,
  PartAFlame,
  DropperAnimation,
  ReagentPourAnimation,
  usePartBTubePositions,
  usePartCKITubeTransform,
  usePartCShakeOffset,
} from './BalancedEquationsAnimations';

export function BalancedEquationsScene3D() {
  const currentPart = useBalancedEquationsStore(s => s.currentPart);
  const partStage = useBalancedEquationsStore(s => s.partStage);
  const cuCO3HeatingProgress = useBalancedEquationsStore(s => s.cuCO3HeatingProgress);
  const limewaterMilkyAmount = useBalancedEquationsStore(s => s.limewaterMilkyAmount);
  const smokeFormationAmount = useBalancedEquationsStore(s => s.smokeFormationAmount);
  const pbI2FormationProgress = useBalancedEquationsStore(s => s.pbI2FormationProgress);
  const tubesAreTouching = useBalancedEquationsStore(s => s.tubesAreTouching);
  const hclPresent = useBalancedEquationsStore(s => s.hclPresent);
  const nh3Present = useBalancedEquationsStore(s => s.nh3Present);
  const pbno3Volume = useBalancedEquationsStore(s => s.pbno3Volume);
  const kiVolume = useBalancedEquationsStore(s => s.kiVolume);
  const mixedTubeContents = useBalancedEquationsStore(s => s.mixedTubeContents);
  const isHeating = useBalancedEquationsStore(s => s.isHeating);
  const flameIntensity = useBalancedEquationsStore(s => s.flameIntensity);
  const isAddingReactant1 = useBalancedEquationsStore(s => s.isAddingReactant1);
  const reactant1Progress = useBalancedEquationsStore(s => s.reactant1Progress);
  const isAddingReactant2 = useBalancedEquationsStore(s => s.isAddingReactant2);
  const reactant2Progress = useBalancedEquationsStore(s => s.reactant2Progress);
  const isBuildingApparatus = useBalancedEquationsStore(s => s.isBuildingApparatus);
  const buildProgress = useBalancedEquationsStore(s => s.buildProgress);
  const isBringingTubesTogether = useBalancedEquationsStore(s => s.isBringingTubesTogether);
  const bringTogetherProgress = useBalancedEquationsStore(s => s.bringTogetherProgress);
  const isPouringMix = useBalancedEquationsStore(s => s.isPouringMix);
  const pourMixProgress = useBalancedEquationsStore(s => s.pourMixProgress);
  const isShaking = useBalancedEquationsStore(s => s.isShaking);
  const shakeProgress = useBalancedEquationsStore(s => s.shakeProgress);
  const simulationSpeed = useBalancedEquationsStore(s => s.simulationSpeed);
  const tick = useBalancedEquationsStore(s => s.tick);

  useFrame((_, delta) => {
    tick(delta * simulationSpeed);
  });

  // ----- PART A apparatus -----
  // Heating test tube held by wooden holder, tilted ~30° over Bunsen flame.
  // Tube position (centre): world (-0.40, 0.05, 0); rotation: slightly tilted toward us
  // Powder pile inside (deeper down in the curved bottom)
  // Limewater tube clamped vertically on the right at world (+0.7, 0.0, 0)
  const cuco3PileMass = partStage.A === 'setup' ? 0 : 1.2;

  // ----- PART B tubes (animated positions) -----
  const partBTubes = usePartBTubePositions(
    isBringingTubesTogether,
    bringTogetherProgress,
    tubesAreTouching,
  );

  // ----- PART C KI tube transform during the pour-into-Pb step -----
  const kiTubeXform = usePartCKITubeTransform(isPouringMix, pourMixProgress, mixedTubeContents);
  const shakeOffset = usePartCShakeOffset(isShaking, shakeProgress);

  return (
    <group>
      <LabRoom />

      {/* ============== PART A ============== */}
      {currentPart === 'A' && (
        <group>
          {/* Bunsen burner under the heating tube. The tube is tilted by
              rotation [0.5, 0, 0] around the X axis, which swings its curved
              bottom back to world z ≈ -0.20 (and slightly below y = -0.27).
              So position the burner UNDER that bottom, and use scale 1.8 so
              the flame is proportional to the test tube (not gigantic). */}
          <group position={[-0.40, -0.585, -0.20]}>
            <BunsenBurner
              position={[0, 0, 0]}
              scale={1.8}
              flameState={isHeating ? 'heating' : 'off'}
              intensity={flameIntensity}
            />
          </group>
          {/* Explicit large, visible flame anchored at the top of the Bunsen
              chimney. The chimney top is at world y = -0.585 + 1.8 * 0.16 ≈
              -0.297. We render the custom flame starting at that height. */}
          <PartAFlame
            position={[-0.40, -0.30, -0.20]}
            active={isHeating}
            intensity={flameIntensity}
          />

          {/* Heating test tube held tilted by the wooden holder. Centre of tube
              roughly at world (-0.40, 0.10, 0), tilted ~28° forward (-Z). */}
          <group position={[-0.40, 0.10, 0]} rotation={[0.5, 0, 0]}>
            <BoilingTube
              position={[0, 0, 0]}
              substanceLevel={0}     // CuCO3 is solid powder, rendered separately
              stateOfMatter="Solid"
            />
            {/* CuCO3 pile sits at the bottom of the curved end. The BoilingTube
                renders its curved bottom at local y ≈ -height/2 (~-0.425), so
                we offset the powder slightly above that. */}
            <CuCO3Powder
              position={[0, -0.40, 0]}
              massG={cuco3PileMass}
              capacityG={1.5}
              heatingProgress={cuCO3HeatingProgress}
              containerRadius={0.05}
            />
            {/* Rubber stopper on top of this tube (only after apparatus built) */}
            {partStage.A !== 'setup' && partStage.A !== 'reactant-1' && (
              <RubberStopper
                visible={true}
                position={[0, 0.45, 0]}
                scale={0.55}
                holeRadius={0.012}
                hideLabel
              />
            )}
          </group>

          {/* Test-tube holder wrapping the tilted tube */}
          <group position={[-0.40, 0.10, 0]} rotation={[0.5, 0, 0]}>
            <TestTubeHolder position={[0, 0.05, 0]} scale={1.0} />
          </group>

          {/* Clamp stand + limewater test tube on the right */}
          <group position={[0.70, -0.585, 0]}>
            <ClampStand visible={true} position={[-0.4, 0, 0]} rotation={[0, Math.PI, 0]} armHeight={1.0} />
            <BoilingTube
              position={[0.28, 0.95, 0]}
              substanceLevel={0.66}      // ~2/3 limewater
              stateOfMatter="Liquid"
            />
            {/* MilkyLiquid overlay drives the visible cloudiness */}
            <MilkyLiquid
              position={[0.28, 0.62, 0]}
              height={0.4}
              radius={0.048}
              milkyAmount={limewaterMilkyAmount}
            />
            {/* CO2 bubbles rising while reaction is happening */}
            {cuCO3HeatingProgress > 0.05 && (
              <HydrogenBubbles
                position={[0.28, 0.55, 0]}
                active={true}
                heightRange={0.32}
                spreadRadius={0.025}
                bubbleCount={40}
                speed={1.3}
              />
            )}
            {/* Stopper at the top of the limewater tube (delivery tube enters here) */}
            {partStage.A !== 'setup' && partStage.A !== 'reactant-1' && (
              <RubberStopper
                visible={true}
                position={[0.28, 1.40, 0]}
                scale={0.55}
                holeRadius={0.012}
                hideLabel
              />
            )}
          </group>

          {/* Delivery rubber tube curving from the heating-tube stopper UP over
              the retort stand and DOWN to the top of the limewater tube's
              stopper. Heating-tube stopper world position = group [-0.40, 0.10, 0]
              + local [0, 0.45, 0] rotated by X-axis 0.5 rad. After rotation:
                y' = 0.45 cos(0.5) ≈ 0.395
                z' = 0.45 sin(0.5) ≈ 0.216
              → world [-0.40, 0.495, 0.216]. Limewater stopper at world
              [0.70 + 0.28, -0.585 + 1.40, 0] = [0.98, 0.815, 0]. */}
          {(partStage.A !== 'setup' && partStage.A !== 'reactant-1') && (
            <>
              <RubberTube
                visible={true}
                fromPos={[-0.40, 0.50, 0.22]}
                toPos={[0.98, 0.86, 0]}
                midOffset={[0.0, 0.55, 0.15]}
                progress={isBuildingApparatus ? buildProgress : 1}
                radius={0.008}
              />
              {/* Vertical glass dip tube that visibly enters the limewater
                  through the stopper and extends down into the liquid. The
                  liquid surface is around world y = 0.50; we end well below
                  it at y = 0.20 so the CO₂ bubbles up through the limewater. */}
              <mesh position={[0.98, 0.49, 0]}>
                <cylinderGeometry args={[0.0075, 0.0075, 0.58, 16]} />
                <meshPhysicalMaterial
                  color="#eef6ff"
                  transparent
                  opacity={0.45}
                  transmission={0.85}
                  roughness={0.05}
                  ior={1.50}
                  side={THREE.DoubleSide}
                  depthWrite={false}
                />
              </mesh>
            </>
          )}

          {/* Pour animation (only while active) */}
          <CuCO3PourAnimation active={isAddingReactant1} progress={reactant1Progress} />
          <PartABuildAnimation active={isBuildingApparatus} progress={buildProgress} />
        </group>
      )}

      {/* ============== PART B ============== */}
      {currentPart === 'B' && (
        <group>
          {/* Two clamp stands flanking the bench — visible only while the tubes
              are in their REST positions (not during/after the bring-together
              animation). Each clamp's grip lines up with the corresponding
              REST tube position via armHeight = 0.94 (so grip y ≈ 0.355). */}
          <ClampStand
            visible={!isBringingTubesTogether && !tubesAreTouching}
            position={[-1.30, -0.585, 0]}
            rotation={[0, Math.PI, 0]}
            armHeight={0.94}
          />
          <ClampStand
            visible={!isBringingTubesTogether && !tubesAreTouching}
            position={[1.30, -0.585, 0]}
            rotation={[0, 0, 0]}
            armHeight={0.94}
          />

          {/* Left tube (HCl) — position + rotation smoothly lerped from REST → TOGETHER */}
          <group position={partBTubes.left} rotation={[0, 0, partBTubes.leftRotZ]}>
            <BoilingTube
              position={[0, 0, 0]}
              substanceLevel={hclPresent ? 0.08 : 0}
              stateOfMatter="Liquid"
            />
            {/* Stopper while the tube sits in the clamp after drops are added */}
            {partStage.B === 'reactant-1' || partStage.B === 'reactant-2' ? (
              <RubberStopper visible={true} position={[0, 0.45, 0]} scale={0.55} holeRadius={0} hideLabel />
            ) : null}
          </group>

          {/* Right tube (NH3) */}
          <group position={partBTubes.right} rotation={[0, 0, partBTubes.rightRotZ]}>
            <BoilingTube
              position={[0, 0, 0]}
              substanceLevel={nh3Present ? 0.08 : 0}
              stateOfMatter="Liquid"
            />
            {partStage.B === 'reactant-2' && !isBringingTubesTogether ? (
              <RubberStopper visible={true} position={[0, 0.45, 0]} scale={0.55} holeRadius={0} hideLabel />
            ) : null}
          </group>

          {/* White smoke cloud — drawn exactly at the midpoint between the two
              tube mouths (computed analytically inside usePartBTubePositions).
              Generous particle count + wider spread so the NH₄Cl cloud is
              clearly visible billowing between the tubes. */}
          <WhiteSmokeCloud
            position={partBTubes.smokeCenter}
            density={smokeFormationAmount}
            bubbleCount={120}
            spreadRadius={0.16}
          />

          {/* Dropper animations */}
          <DropperAnimation active={isAddingReactant1} progress={reactant1Progress} reactant="HCl" />
          <DropperAnimation active={isAddingReactant2} progress={reactant2Progress} reactant="NH3" />
        </group>
      )}

      {/* ============== PART C ============== */}
      {currentPart === 'C' && (
        <group>
          {/* Two clamp stands */}
          <group position={[-0.45, -0.585, 0]}>
            <ClampStand visible={true} position={[0.3, 0, 0]} armHeight={1.0} />
          </group>
          <group position={[0.45, -0.585, 0]}>
            <ClampStand visible={!isPouringMix} position={[-0.3, 0, 0]} rotation={[0, Math.PI, 0]} armHeight={1.0} />
          </group>

          {/* Left tube — Pb(NO3)2 (and later the mixed product). Apply shake offset. */}
          <group position={[-0.45 + shakeOffset[0], 0.0, 0]}>
            <BoilingTube
              position={[0, 0, 0]}
              substanceLevel={
                mixedTubeContents === 'mixed' ? 0.30 :
                pbno3Volume > 0 ? 0.15 : 0
              }
              stateOfMatter="Liquid"
            />
            {mixedTubeContents === 'mixed' && (
              <YellowPrecipitate
                position={[0, -0.30, 0]}
                formationProgress={pbI2FormationProgress}
                containerRadius={0.045}
                columnHeight={0.20}
                particleCount={120}
                swirling={isShaking}
              />
            )}
          </group>

          {/* Right tube — KI. Position interpolated during the pour animation. */}
          <group position={kiTubeXform.position} rotation={kiTubeXform.rotation}>
            <BoilingTube
              position={[0, 0, 0]}
              substanceLevel={
                kiVolume > 0 && mixedTubeContents !== 'mixed' ? 0.15 : 0
              }
              stateOfMatter="Liquid"
            />
          </group>

          {/* Pour streams */}
          <ReagentPourAnimation active={isAddingReactant1} progress={reactant1Progress} reactant="PbNO3" />
          <ReagentPourAnimation active={isAddingReactant2} progress={reactant2Progress} reactant="KI" />

          {/* Stream when pouring KI into Pb tube */}
          {kiTubeXform.showStream && (
            <mesh position={[(kiTubeXform.position[0] + (-0.45)) / 2, 0.15, 0]}>
              <cylinderGeometry args={[0.005, 0.012, Math.abs(kiTubeXform.position[1] - 0.0) + 0.2, 12]} />
              <meshPhysicalMaterial color="#e8f4ff" transparent opacity={0.8} transmission={0.85} ior={1.34} roughness={0.05} depthWrite={false} />
            </mesh>
          )}
        </group>
      )}
    </group>
  );
}

export default BalancedEquationsScene3D;
